/* ============================================================
   Контакты: телефон/Telegram/адрес + соцсети + форма сообщения.
   Форма уходит в бэкенд через существующий support-канал.
   ============================================================ */
import { useState } from 'react';
import { useAppStore } from '../../store';
import { useL } from '../store';
import { useFedContent } from '../content';
import { SOCIALS } from '../data';
import { Ic } from '../icons';
import { Rv, PageHead, Field } from '../ui';

function ContactCard({ icon, label, value, href }) {
  const inner = (
    <>
      <span className="flex-shrink-0 flex items-center justify-center rounded-[11px] text-accent-bright" style={{ width: 46, height: 46, background: 'rgba(225,18,23,.09)', border: '1px solid var(--fed-line-accent)' }}>
        <Ic name={icon} size={22} />
      </span>
      <div>
        <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 10, letterSpacing: '.1em' }}>{label}</div>
        <div className="font-semibold text-fg" style={{ fontSize: 16, marginTop: 3 }}>{value}</div>
      </div>
    </>
  );
  const cls = 'flex items-center gap-[15px] rounded-[14px] bg-surface border border-line no-underline';
  return href ? (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className={cls} style={{ padding: 18 }}>{inner}</a>
  ) : (
    <div className={cls} style={{ padding: 18 }}>{inner}</div>
  );
}

export default function FedContacts() {
  const L = useL();
  const settings = useFedContent((s) => s.settings);
  const submitSupportTicket = useAppStore((s) => s.submitSupportTicket);
  const fire = useAppStore((s) => s.fire);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const phone = settings.phone || '+7 700 678 0221';
  const telegram = settings.telegram || '@fc_sko';
  const email = settings.email || 'admin@sko-esports.kz';
  const city = settings.city || 'Петропавловск';
  const telegramHandle = telegram.replace(/^https?:\/\/t\.me\//i, '').replace(/^@/, '');
  const telegramHref = /^https?:\/\//i.test(telegram) ? telegram : `https://t.me/${telegramHandle}`;
  const phoneHref = `tel:${phone.replace(/[^+\d]/g, '')}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await submitSupportTicket({ name: form.name, email: form.email, phone: '', message: form.message, page: 'contacts' });
      setSent(true);
    } catch (err) {
      // тикет мог не уйти (нет сети/бэкенда) — покажем успех локально не будем, честно скажем
      fire && fire('Не удалось отправить — попробуйте позже', 'error');
    }
    setSending(false);
  };

  return (
    <div className="fed-pagewrap max-w-[1320px] mx-auto" style={{ padding: '52px 24px 72px' }}>
      <PageHead eyebrow={settings.name || L.brandTop} title={L.ct_h1} sub={L.ct_sub} mb={32} />

      <div className="fed-split-2">
        <Rv className="flex flex-col gap-3.5">
          <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.2em' }}>{L.ct_reach}</div>
          <ContactCard icon="phone" label={L.au_phone} value={phone} href={phoneHref} />
          <ContactCard icon="telegram" label="Telegram" value={telegram} href={telegramHref} />
          <ContactCard icon="inbox" label="Email" value={email} href={`mailto:${email}`} />
          <ContactCard icon="pin" label={L.lbl_place} value={city} />
          <div style={{ marginTop: 8 }}>
            <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.2em', marginBottom: 12 }}>{L.ct_social}</div>
            <div className="flex gap-2.5">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.name}
                  className="rounded-[11px] flex items-center justify-center bg-surface-2 border border-line text-fg-soft transition-colors hover:!text-accent-bright hover:border-line-accent"
                  style={{ width: 46, height: 46 }}
                >
                  <Ic name={s.icon} size={20} />
                </a>
              ))}
            </div>
          </div>
        </Rv>

        <Rv delay={0.08} className="rounded-[18px] bg-surface border border-line" style={{ padding: 28 }}>
          {!sent ? (
            <>
              <h3 style={{ fontSize: 20, marginBottom: 18 }}>{L.ct_form_t}</h3>
              <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
                <Field label={L.ct_name}>
                  <input className="fed-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </Field>
                <Field label={L.ct_email}>
                  <input className="fed-input" type="text" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </Field>
                <Field label={L.ct_msg}>
                  <textarea className="fed-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
                </Field>
                <button type="submit" disabled={sending} className="fed-btn-primary h-[50px] rounded-[11px]" style={{ fontSize: 15, opacity: sending ? 0.7 : 1 }}>
                  {sending ? '…' : L.ct_send}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center flex flex-col items-center gap-3.5" style={{ padding: '30px 0' }}>
              <div className="rounded-full flex items-center justify-center" style={{ width: 74, height: 74, background: 'rgba(67,217,106,.14)', border: '1px solid rgba(67,217,106,.45)', color: 'var(--fed-ok)' }}>
                <Ic name="check" size={34} sw={2.4} />
              </div>
              <h3 className="font-body font-extrabold text-fg" style={{ fontSize: 24, textTransform: 'none', letterSpacing: 0 }}>{L.sp_ok_t}</h3>
              <p className="text-fg-muted" style={{ fontSize: 15 }}>{L.sp_ok_d}</p>
            </div>
          )}
        </Rv>
      </div>
    </div>
  );
}
